import _ from 'lodash'
import { GridApi } from 'ag-grid-community'
import { Dependency } from './DependencyContext'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import DateVO from '../../../../../../../../../../vo/DateVO'
import objects from '../../../../../../../../../../utils/objects'

type CascadeDirection = 'forward' | 'backward'

type CollectCascadedRowsParams = {
  sourceWbsItemUuids: Set<string>
  workdayDelta: number
  direction: CascadeDirection
  dependencies: Dependency[]
  wbsItemUuidToRowIndexMap: ReadonlyMap<string, number>
  api: GridApi
  scale: CalendarDateVO[]
  dataPath: string
}

/**
 * 稼働日差分を算出する。
 * oldDate → newDate の間に何稼働日分のシフトがあるかを、
 * CalendarDateVO.nextWorkDay を使って数える。
 */
export const countWorkdayDelta = (
  scale: CalendarDateVO[],
  oldDate: CalendarDateVO,
  newDate: CalendarDateVO
): number => {
  if (oldDate.isEqual(newDate)) return 0

  const forward = newDate.isAfter(oldDate)
  let count = 0
  let current = oldDate

  if (forward) {
    while (!current.isEqual(newDate)) {
      const next = CalendarDateVO.nextWorkDay(scale, current, 1)
      if (!next || next.isEqual(current)) break
      count++
      current = next
      if (current.isAfter(newDate)) {
        break
      }
    }
  } else {
    while (!current.isEqual(newDate)) {
      const prev = CalendarDateVO.previousWorkDay(scale, current, 1)
      if (!prev || prev.isEqual(current)) break
      count--
      current = prev
      if (current.isBefore(newDate)) {
        break
      }
    }
  }
  return count
}

// カスケードを打ち切るステータス（WbsItemStatus.DONE, WbsItemStatus.DISCARD）
const TERMINAL_STATUSES: ReadonlySet<string> = new Set(['DONE', 'DISCARD'])

/**
 * 稼働日数分だけ日付をシフトする。
 * 正の値: nextWorkDay（後ろへ）、負の値: previousWorkDay（前へ）
 */
const shiftByWorkdays = (
  scale: CalendarDateVO[],
  date: CalendarDateVO,
  delta: number
): CalendarDateVO => {
  if (delta === 0) return date
  if (delta > 0) {
    return CalendarDateVO.nextWorkDay(scale, date, delta) ?? date
  }
  return CalendarDateVO.previousWorkDay(scale, date, Math.abs(delta)) ?? date
}

/**
 * FS依存関係のカスケードスライド。
 *
 * - forward: 始点のscheduledEndが変動 → 後続WBSアイテムを同じ稼働日数分シフト
 * - backward: 終点のscheduledStartが変動 → 先行WBSアイテムを同じ稼働日数分シフト
 *
 * Done/DiscardのWBSアイテムに到達したらカスケードを打ち切る。
 */
export const collectCascadedRows = ({
  sourceWbsItemUuids,
  workdayDelta,
  direction,
  dependencies,
  wbsItemUuidToRowIndexMap,
  api,
  scale,
  dataPath,
}: CollectCascadedRowsParams): Record<string, unknown>[] => {
  if (workdayDelta === 0) return []

  // 隣接リスト構築: カスケード方向に応じて辿る向きを変える
  const adjacency = new Map<string, string[]>()
  for (const dep of dependencies) {
    if (!dep.fromWbsItemUuid || !dep.toWbsItemUuid) continue
    const [source, target] =
      direction === 'forward'
        ? [dep.fromWbsItemUuid, dep.toWbsItemUuid]
        : [dep.toWbsItemUuid, dep.fromWbsItemUuid]
    const list = adjacency.get(source)
    if (list) {
      list.push(target)
    } else {
      adjacency.set(source, [target])
    }
  }

  // BFS
  const visited = new Set<string>(sourceWbsItemUuids)
  const queue: string[] = []

  for (const uuid of sourceWbsItemUuids) {
    const neighbors = adjacency.get(uuid)
    if (neighbors) {
      for (const n of neighbors) {
        if (!visited.has(n)) {
          visited.add(n)
          queue.push(n)
        }
      }
    }
  }

  const cascadedRows: Record<string, unknown>[] = []
  const prefix = dataPath ? dataPath + '.' : ''

  while (queue.length > 0) {
    const currentUuid = queue.shift()!

    // 行データ取得（ステータス判定に必要）
    const rowIndex = wbsItemUuidToRowIndexMap.get(currentUuid)
    const node =
      rowIndex !== undefined ? api.getDisplayedRowAtIndex(rowIndex) : undefined
    const wbsItem = node?.data
      ? objects.getValue(node.data, `${prefix}wbsItem`)
      : undefined

    // Done/Discard: カスケードを打ち切り（後続/先行にも伝播しない）
    if (wbsItem?.status && TERMINAL_STATUSES.has(wbsItem.status)) {
      continue
    }

    // Done/Discard以外: BFS走査を継続（日付なしやグリッド外でも伝播する）
    const neighbors = adjacency.get(currentUuid)
    if (neighbors) {
      for (const n of neighbors) {
        if (!visited.has(n)) {
          visited.add(n)
          queue.push(n)
        }
      }
    }

    if (!node?.data || rowIndex === undefined) continue

    const row = _.cloneDeep(node.data)
    const rowWbsItem = objects.getValue(row, `${prefix}wbsItem`)
    if (!rowWbsItem?.scheduledDate) continue

    const { startDate, endDate } = rowWbsItem.scheduledDate
    if (!startDate && !endDate) continue

    // 稼働日シフト
    const currentStart = CalendarDateVO.of(new Date(startDate || endDate))
    const currentEnd = CalendarDateVO.of(new Date(endDate || startDate))

    const newStart = shiftByWorkdays(scale, currentStart, workdayDelta)
    const newEnd = shiftByWorkdays(scale, currentEnd, workdayDelta)

    objects.setValue(row, `${prefix}wbsItem.scheduledDate`, {
      startDate: new DateVO(newStart.date).serialize(),
      endDate: new DateVO(newEnd.date).serialize(),
    })
    row.isEdited = true
    cascadedRows.push(row)
  }

  return cascadedRows
}
